// src/pages/finance-portal/fixed-assets/Reports.js
//
// Reports: roll-forward, fully-depreciated in use,
// by-location, tax depreciation schedule

import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Card, CardContent, CircularProgress, Grid,
    MenuItem, Paper, Skeleton, Tab, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useSnackbar } from 'notistack';

import axiosInstance from '../../../utils/axiosInstance';
import {
    FA_FONT, FA_ACCENT, FA_ACCENT_DARK,
    formatCurrency, humanizeStatus,
} from './components/fixedAssetFormatters';

const headSx = { fontFamily: FA_FONT, fontWeight: 700, fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' };
const cellSx = { fontFamily: FA_FONT, fontSize: '0.84rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9' };

export default function Reports() {
    const { enqueueSnackbar } = useSnackbar();
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);

    const [year, setYear] = useState(new Date().getFullYear());
    const [rollForward, setRollForward] = useState(null);
    const [fullyDepreciated, setFullyDepreciated] = useState([]);
    const [byLocation, setByLocation] = useState([]);
    const [taxSchedule, setTaxSchedule] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            if (tab === 0) {
                const r = await axiosInstance.get('/api/finance/fixed-assets/reports/roll-forward',
                    { params: { start_date: `${year}-01-01`, end_date: `${year}-12-31` } });
                setRollForward(r.data?.data || null);
            } else if (tab === 1) {
                const r = await axiosInstance.get('/api/finance/fixed-assets/reports/fully-depreciated-in-use');
                setFullyDepreciated(r.data?.data || []);
            } else if (tab === 2) {
                const r = await axiosInstance.get('/api/finance/fixed-assets/reports/by-location');
                setByLocation(r.data?.data || []);
            } else if (tab === 3) {
                const r = await axiosInstance.get('/api/finance/fixed-assets/reports/tax-depreciation',
                    { params: { year } });
                setTaxSchedule(r.data?.data || null);
            }
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Report failed', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [tab, year, enqueueSnackbar]);

    useEffect(() => { load(); }, [load]);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: `linear-gradient(135deg, ${FA_ACCENT} 0%, ${FA_ACCENT_DARK} 100%)`,
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: 3, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AssessmentIcon sx={{ fontSize: 30, color: '#fff' }} />
                        <Typography sx={{ fontFamily: FA_FONT, fontWeight: 800, color: 'white', fontSize: '1.6rem' }}>
                            Asset Reports
                        </Typography>
                    </Box>
                    <TextField size="small" type="number" label="Year" value={year}
                        onChange={e => setYear(e.target.value)}
                        sx={{ maxWidth: 130, '& label, & input': { color: 'white' },
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' } }}
                        InputLabelProps={{ sx: { fontFamily: FA_FONT } }}
                        InputProps={{ sx: { fontFamily: FA_FONT, color: '#fff' } }} />
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1300, mx: 'auto' }}>
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #e2e8f0', px: 2 }}
                        TabIndicatorProps={{ style: { backgroundColor: FA_ACCENT } }}>
                        <Tab label="Roll-Forward" sx={{ fontFamily: FA_FONT, textTransform: 'none', fontWeight: 600 }} />
                        <Tab label="Fully Depreciated In Use" sx={{ fontFamily: FA_FONT, textTransform: 'none', fontWeight: 600 }} />
                        <Tab label="By Location" sx={{ fontFamily: FA_FONT, textTransform: 'none', fontWeight: 600 }} />
                        <Tab label="Tax Depreciation" sx={{ fontFamily: FA_FONT, textTransform: 'none', fontWeight: 600 }} />
                    </Tabs>

                    <CardContent sx={{ p: 3 }}>
                        {loading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress sx={{ color: FA_ACCENT }} /></Box>}

                        {!loading && tab === 0 && rollForward && (
                            <Grid container spacing={2}>
                                {[
                                    ['Beginning Cost', rollForward.beginning_cost],
                                    ['Additions', rollForward.additions_cost],
                                    ['Disposals', rollForward.disposed_cost],
                                    ['Ending Cost', rollForward.ending_cost],
                                    ['Beginning Acc. Dep.', rollForward.beginning_accumulated_depreciation],
                                    ['Period Depreciation', rollForward.period_depreciation],
                                    ['Ending Acc. Dep.', rollForward.ending_accumulated_depreciation],
                                    ['Beginning Book Value', rollForward.beginning_book_value],
                                    ['Ending Book Value', rollForward.ending_book_value],
                                ].map(([label, value]) => (
                                    <Grid item xs={6} md={4} key={label}>
                                        <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.72rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>{label}</Typography>
                                        <Typography sx={{ fontFamily: FA_FONT, fontSize: '1.05rem', fontWeight: 600, color: '#0f172a' }}>{formatCurrency(value)}</Typography>
                                    </Grid>
                                ))}
                            </Grid>
                        )}

                        {!loading && tab === 1 && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            {['Asset #', 'Name', 'Category', 'Cost', 'Acc. Dep.', 'Book Value', 'Years In Service', 'Location'].map(h => (
                                                <TableCell key={h} sx={headSx}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {fullyDepreciated.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                                    <Typography sx={{ fontFamily: FA_FONT, color: '#94a3b8' }}>None</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : fullyDepreciated.map(a => (
                                            <TableRow key={a.asset_id}>
                                                <TableCell sx={cellSx}>{a.asset_number}</TableCell>
                                                <TableCell sx={cellSx}>{a.name}</TableCell>
                                                <TableCell sx={cellSx}>{a.category || '\u2014'}</TableCell>
                                                <TableCell sx={cellSx}>{formatCurrency(a.cost_basis)}</TableCell>
                                                <TableCell sx={cellSx}>{formatCurrency(a.accumulated_depreciation)}</TableCell>
                                                <TableCell sx={cellSx}>{formatCurrency(a.book_value)}</TableCell>
                                                <TableCell sx={cellSx}>{a.years_in_service ?? '\u2014'}</TableCell>
                                                <TableCell sx={cellSx}>{a.location || '\u2014'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {!loading && tab === 2 && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            {['Location', 'Department', 'Asset Count', 'Total Cost', 'Total Acc. Dep.', 'Book Value'].map(h => (
                                                <TableCell key={h} sx={headSx}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {byLocation.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                                                    <Typography sx={{ fontFamily: FA_FONT, color: '#94a3b8' }}>No data</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : byLocation.map((r, i) => (
                                            <TableRow key={i}>
                                                <TableCell sx={cellSx}>{r.location}</TableCell>
                                                <TableCell sx={cellSx}>{r.department}</TableCell>
                                                <TableCell sx={cellSx}>{r.asset_count}</TableCell>
                                                <TableCell sx={cellSx}>{formatCurrency(r.total_cost)}</TableCell>
                                                <TableCell sx={cellSx}>{formatCurrency(r.total_accumulated_depreciation)}</TableCell>
                                                <TableCell sx={cellSx}>{formatCurrency(r.total_book_value)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {!loading && tab === 3 && taxSchedule && (
                            <Box>
                                <Typography sx={{ fontFamily: FA_FONT, fontWeight: 700, color: '#0f172a', mb: 2 }}>
                                    Year {taxSchedule.year} — Total {formatCurrency(taxSchedule.total_year_depreciation)}
                                    {' · '} Section 179 {formatCurrency(taxSchedule.total_section_179)}
                                </Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                {['Asset', 'Method', 'Recovery', 'Cost', 'Section 179', 'Bonus %', 'Year Dep.', 'Accumulated'].map(h => (
                                                    <TableCell key={h} sx={headSx}>{h}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(taxSchedule.rows || []).length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                                        <Typography sx={{ fontFamily: FA_FONT, color: '#94a3b8' }}>
                                                            No tax depreciation entries for this year.
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : taxSchedule.rows.map(r => (
                                                <TableRow key={r.asset_id}>
                                                    <TableCell sx={cellSx}>{r.asset_number} {r.asset_name}</TableCell>
                                                    <TableCell sx={cellSx}>{r.tax_method}</TableCell>
                                                    <TableCell sx={cellSx}>{r.recovery_years ? `${r.recovery_years} yr` : '\u2014'}</TableCell>
                                                    <TableCell sx={cellSx}>{formatCurrency(r.cost_basis)}</TableCell>
                                                    <TableCell sx={cellSx}>{formatCurrency(r.section_179)}</TableCell>
                                                    <TableCell sx={cellSx}>{r.bonus_pct}%</TableCell>
                                                    <TableCell sx={cellSx}>{formatCurrency(r.year_depreciation)}</TableCell>
                                                    <TableCell sx={cellSx}>{formatCurrency(r.accumulated)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}
